<template>
    <div>
      <SettingHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Notifications</h5>
          <p class="page-sub">Choose which events trigger email and WhatsApp notifications</p>
        </div>
      </div>
  
      <!-- Data card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading notification settings...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status === 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to manage notifications.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="notifications.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="bell" /></div>
          <p class="state-title">No notifications configured</p>
          <p class="state-sub">Notification settings will appear here once added.</p>
        </div>
  
        <template v-else>
  
          <!-- Column headers -->
          <div class="notif-header">
            <span class="notif-header-event">Notification Event</span>
            <div class="notif-header-channels">
              <div class="channel-label">
                <font-awesome-icon icon="envelope" class="me-1" /> Email
              </div>
              <div class="channel-label">
                <font-awesome-icon :icon="['fab', 'whatsapp']" class="me-1" /> WhatsApp
              </div>
            </div>
          </div>
  
          <!-- Notification rows -->
          <div
            v-for="(notification, index) in notifications"
            :key="notification.id"
            class="notif-row"
          >
            <div class="notif-info">
              <div class="notif-icon">
                <font-awesome-icon :icon="notifIcon(notification.tag)" />
              </div>
              <div>
                <div class="notif-tag">{{ formatTag(notification.tag) }}</div>
                <div class="notif-desc">{{ notifDesc(notification.tag) }}</div>
              </div>
            </div>
  
            <div class="notif-toggles">
  
              <!-- Email toggle -->
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  :true-value="1"
                  :false-value="0"
                  v-model="email_statuses[index]"
                  @change="updateEmailStatus(notification.id, $event)"
                />
                <span class="toggle-track"><span class="toggle-thumb"></span></span>
                <span class="toggle-label" :class="email_statuses[index] ? 'on' : 'off'">
                  {{ email_statuses[index] ? 'On' : 'Off' }}
                </span>
              </label>
  
              <!-- WhatsApp toggle -->
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  :true-value="1"
                  :false-value="0"
                  v-model="whatsapp_statuses[index]"
                  @change="updateWhatsappStatus(notification.id, $event)"
                />
                <span class="toggle-track toggle-track--green"><span class="toggle-thumb"></span></span>
                <span class="toggle-label" :class="whatsapp_statuses[index] ? 'on' : 'off'">
                  {{ whatsapp_statuses[index] ? 'On' : 'Off' }}
                </span>
              </label>
  
            </div>
          </div>
  
        </template>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, onMounted } from 'vue';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import SettingHeaderTab from './components/settingheadertab.vue';
  
  export default {
    name: 'NotificationSettings',
    components: { SettingHeaderTab },
  
    setup() {
      const toast   = useToast();
      const loading = ref(false);
  
      const notifications      = ref([]);
      const email_statuses     = ref([]);
      const whatsapp_statuses  = ref([]);
      const permission_status  = ref(null);
  
      // ── Helpers ──────────────────────────────────────────────────────
      const formatTag = (tag) => {
        if (!tag) return '';
        return tag.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      };
  
      const notifIcon = (tag) => {
        if (!tag) return 'bell';
        const t = tag.toLowerCase();
        if (t.includes('sale') || t.includes('order'))    return 'shopping-cart';
        if (t.includes('payment') || t.includes('invoice')) return 'credit-card';
        if (t.includes('stock') || t.includes('inventory')) return 'boxes';
        if (t.includes('user') || t.includes('login'))    return 'user';
        if (t.includes('customer'))                        return 'users';
        if (t.includes('supplier'))                        return 'truck';
        if (t.includes('report'))                          return 'chart-bar';
        if (t.includes('expiry') || t.includes('expire')) return 'clock';
        return 'bell';
      };
  
      const notifDesc = (tag) => {
        if (!tag) return '';
        const t = tag.toLowerCase();
        if (t.includes('sale'))      return 'Triggered when a sale is completed';
        if (t.includes('payment'))   return 'Triggered when a payment is received';
        if (t.includes('stock'))     return 'Triggered when stock levels change';
        if (t.includes('login'))     return 'Triggered when a user logs in';
        if (t.includes('customer'))  return 'Triggered for customer-related events';
        if (t.includes('supplier'))  return 'Triggered for supplier-related events';
        if (t.includes('expiry'))    return 'Triggered when something is about to expire';
        return 'Manage this notification event';
      };
  
      // ── Data ─────────────────────────────────────────────────────────
      const getAllNotifications = async () => {
        loading.value = true;
        try {
          const { data } = await axios.get('settings/set-notification');
          notifications.value     = data.data;
          email_statuses.value    = data.data.map(n => n.email);
          whatsapp_statuses.value = data.data.map(n => n.whatsapp);
        } catch (err) {
          if (err?.response?.status === 403) {
            permission_status.value = 403;
            toast.error(err.response.data.message);
          } else {
            toast.error('Could not load notification settings.');
          }
        } finally {
          loading.value = false;
        }
      };
  
      // ── Status updates ────────────────────────────────────────────────
      const updateEmailStatus = async (id, evt) => {
        try {
          const { data } = await axios.put('settings/notification/change-email-status/' + id, { value: evt.target.checked });
          toast.success(data.message);
        } catch (err) {
          await getAllNotifications(); // revert on error
          const status = err.response?.status;
          if (status === 422) toast.warning('Invalid request.');
          else toast.error('Could not update email notification.');
        }
      };
  
      const updateWhatsappStatus = async (id, evt) => {
        try {
          const { data } = await axios.put('settings/notification/change-whatsapp-status/' + id, { value: evt.target.checked });
          toast.success(data.message);
        } catch (err) {
          await getAllNotifications(); // revert on error
          const status = err.response?.status;
          if (status === 422) toast.warning('Invalid request.');
          else toast.error('Could not update WhatsApp notification.');
        }
      };
  
      onMounted(getAllNotifications);
  
      return {
        loading, notifications, email_statuses, whatsapp_statuses, permission_status,
        formatTag, notifIcon, notifDesc,
        updateEmailStatus, updateWhatsappStatus,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Data card ───────────────────────────────────────────────────────── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ──────────────────────────────────────────────────────────── */
  .state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
  .state-icon--red  { background:#fff0f0; color:#e03535; }
  .state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub        { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell  { color:#4a5e78; }
  .mt-3 { margin-top:12px; }
  
  /* ── Column header ───────────────────────────────────────────────────── */
  .notif-header { display:flex; align-items:center; justify-content:space-between; padding:12px 24px; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
  .notif-header-event { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; }
  .notif-header-channels { display:flex; gap:40px; }
  .channel-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; display:flex; align-items:center; min-width:80px; justify-content:center; }
  
  /* ── Notification row ────────────────────────────────────────────────── */
  .notif-row { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .notif-row:last-child { border-bottom:none; }
  .notif-row:hover { background:#f8fafc; }
  
  /* ── Notification info ───────────────────────────────────────────────── */
  .notif-info { display:flex; align-items:center; gap:14px; flex:1; }
  .notif-icon { width:40px; height:40px; border-radius:10px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .notif-tag  { font-size:14px; font-weight:600; color:#1a2b45; margin-bottom:3px; }
  .notif-desc { font-size:12px; color:#7a90b0; }
  
  /* ── Toggles ─────────────────────────────────────────────────────────── */
  .notif-toggles { display:flex; gap:24px; align-items:center; }
  
  /* ── Toggle switch ───────────────────────────────────────────────────── */
  .toggle-switch       { display:inline-flex; flex-direction:column; align-items:center; gap:5px; cursor:pointer; user-select:none; min-width:80px; }
  .toggle-switch input { display:none; }
  .toggle-track        { position:relative; width:44px; height:24px; border-radius:24px; background:#d1d9e8; transition:background .2s; }
  .toggle-switch input:checked + .toggle-track { background:#004AAD; }
  .toggle-track--green { }
  .toggle-switch input:checked + .toggle-track--green { background:#25d366; }
  .toggle-thumb        { position:absolute; top:4px; left:4px; width:16px; height:16px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.2); transition:transform .2s; }
  .toggle-switch input:checked + .toggle-track .toggle-thumb  { transform:translateX(20px); }
  .toggle-switch input:checked + .toggle-track--green .toggle-thumb { transform:translateX(20px); }
  .toggle-label     { font-size:11px; font-weight:600; }
  .toggle-label.on  { color:#16a34a; }
  .toggle-label.off { color:#7a90b0; }
  
  /* ── Util ────────────────────────────────────────────────────────────── */
  .me-1 { margin-right:4px; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:768px) {
    .page-header  { flex-direction:column; }
    .notif-row    { flex-direction:column; align-items:flex-start; gap:16px; }
    .notif-header-channels { gap:20px; }
    .notif-toggles { gap:16px; }
  }
  </style>